import _ from 'lodash'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { useRequestHandler } from '@/hooks/useRequestHandler'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import { EstablishmentsTable } from '../components/EstablishmentsTable'

import { useAppSelector } from '@/redux/rootReducer'
import {
  assignProfessionalsToOrdoclicEntity,
  createAdmin,
  createOrdoclicEntity,
  deleteEstablishment,
  selectAdminCreateRequestTracker,
  selectAdminDeleteRequestTracker,
  selectAdminUpdateRequestTracker,
  selectAssignProfessionalsRequestTracker,
  selectEstablishments,
  selectOrdoclicEntityCreateRequestTracker,
  updateEstablishment,
} from '@/redux/reducers/admin.reducer'
import { StatsContainer } from '@/pages/admin/statistics'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import CreateOrdoclicEntityDialog from '../components/CreateOrdoclicEntityDialog'
import CreateEstablishmentDialogContainer from './CreateEstablishmentDialog'
import { StructureActivityCode } from '@/core/models/establishment.model'
import { EstablishmentFormData } from '@/features/establishment'

export function EstablishmentsTableContainer() {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const deleteEstablishmentRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.establishment.delete.success'), { variant: 'success' })
      setDeleteConfirmationDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.establishment.delete.error'), { variant: 'error' }),
    requestTrackerSelector: selectAdminDeleteRequestTracker,
  })

  const updateEstablishmentRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.establishment.update.success'), { variant: 'success' })
      setEstablishmentDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.establishment.update.error'), { variant: 'error' }),
    requestTrackerSelector: selectAdminUpdateRequestTracker,
  })

  const [createAdminDialogOpen, setCreateAdminDialogOpen] = useState<boolean>(false)
  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>()

  const [showStatsDialogOpen, setShowStatsDialogOpen] = useState<boolean>(false)
  const [selectedEstablishmentIdForStats, setSelectedEstablishmentIdForStats] = useState<string>('')

  const [establishmentDialogOpen, setEstablishmentDialogOpen] = useState<boolean>(false)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)

  const establishments = useAppSelector(selectEstablishments)

  const establishmentsData = useMemo(
    () =>
      _.map(establishments, (establishment) => ({
        id: establishment._id,
        active: establishment.active || false,
        name: establishment.name,
        ordoclicEntityId: establishment.ordoclicEntityId || undefined,
      })),
    [establishments]
  )

  const handleShowStats = (establishmentId: string) => {
    setSelectedEstablishmentIdForStats(establishmentId)
    setShowStatsDialogOpen(true)
  }

  const handleCloseStatsDialog = () => {
    setShowStatsDialogOpen(false)
  }

  const handleCloseCreateAdminDialog = () => {
    setCreateAdminDialogOpen(false)
    if (selectedEstablishmentId) setSelectedEstablishmentId(undefined)
  }

  const handleCloseDeleteConfimrationDialog = () => {
    setDeleteConfirmationDialogOpen(false)
    if (selectedEstablishmentId) setSelectedEstablishmentId(undefined)
  }

  const handleClickCreateAdmin = (establishmentId: string) => {
    setCreateAdminDialogOpen(true)
    setSelectedEstablishmentId(establishmentId)
  }

  const handleClickDeleteEstablishment = (establishmentId: string) => {
    setDeleteConfirmationDialogOpen(true)
    setSelectedEstablishmentId(establishmentId)
  }

  const handleClickCreateEstablishment = () => {
    setIsEditMode(false)
    setSelectedEstablishmentId(undefined)
    setEstablishmentDialogOpen(true)
  }

  const handleClickEditEstablishment = (establishmentId: string) => {
    setIsEditMode(true)
    setSelectedEstablishmentId(establishmentId)
    setEstablishmentDialogOpen(true)
  }

  const handleCloseEstablishmentDialog = () => {
    setEstablishmentDialogOpen(false)
    setSelectedEstablishmentId(undefined)
    setIsEditMode(false)
  }

  const handleUpdateEstablishment = (values: EstablishmentFormData) => {
    if (isEditMode && selectedEstablishmentId) {
      // Dispatch update action here
      updateEstablishmentRequestHandler.dispatch(
        updateEstablishment({
          etablissementId: selectedEstablishmentId,
          updateEstablishmentDto: {
            name: values.name,
            finess: values.finess,
            address: values.address,
            zipCode: values.zipCode,
            city: values.city,
            country: values.country,
            phone: values.phone,
            preferredLanguage: values.preferredLanguage || null,
            structureActivity: {
              code: values.structureActivity as StructureActivityCode,
            },
            ordoclicVidalLicense:
              values.ordoclicVidalLicense?.id && values.ordoclicVidalLicense?.secret
                ? {
                    id: values.ordoclicVidalLicense.id,
                    secret: values.ordoclicVidalLicense.secret,
                  }
                : undefined,
          },
        })
      )
    }
    handleCloseEstablishmentDialog()
  }

  const routeToSubscriptions = (establishmentId: string) => {
    navigate('/super-admin/subscriptions/' + establishmentId)
  }

  const handleDeleteEstablishment = () => {
    if (!selectedEstablishmentId) return
    deleteEstablishmentRequestHandler.dispatch(deleteEstablishment(selectedEstablishmentId))
  }

  const [createOrdoclicEntityDialogOpen, setCreateOrdoclicEntityDialogOpen] = useState<boolean>(false)
  const [assignProfessionalsDialogOpen, setAssignProfessionalsDialogOpen] = useState<boolean>(false)

  const createOrdoclicEntityRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.ordoclicEntity.create.success'), { variant: 'success' })
      setCreateOrdoclicEntityDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.ordoclicEntity.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectOrdoclicEntityCreateRequestTracker,
  })

  const assignProfessionalsRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.ordoclicEntity.assignProfessionals.success'), { variant: 'success' })
      setAssignProfessionalsDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.ordoclicEntity.assignProfessionals.error'), { variant: 'error' }),
    requestTrackerSelector: selectAssignProfessionalsRequestTracker,
  })

  const handleCreateOrdoclicEntity = (values: { name: string; finess?: string }) => {
    if (!selectedEstablishmentId) return
    createOrdoclicEntityRequestHandler.dispatch(
      createOrdoclicEntity({
        establishmentId: selectedEstablishmentId,
        ...values,
      })
    )
  }

  const handleAssignProfessionals = (id: string) => {
    assignProfessionalsRequestHandler.dispatch(assignProfessionalsToOrdoclicEntity(id))
  }

  const handleClickCreateOrdoclicEntity = (establishmentId: string) => {
    setCreateOrdoclicEntityDialogOpen(true)
    setSelectedEstablishmentId(establishmentId)
  }

  const handleClickAssignProfessionals = (establishmentId: string) => {
    handleAssignProfessionals(establishmentId)
  }

  return (
    <>
      <EstablishmentsTable
        data={establishmentsData}
        onDeleteEstablishment={handleClickDeleteEstablishment}
        onShowStats={handleShowStats}
        onShowSubscriptions={routeToSubscriptions}
        onCreateOrdoclicEntity={handleClickCreateOrdoclicEntity}
        onAssignProfessionals={handleClickAssignProfessionals}
        onEditEstablishment={handleClickEditEstablishment}
      />
      <DeleteConfirmationDialog
        isDeleting={deleteEstablishmentRequestHandler.requestTracker.loading}
        open={deleteConfirmationDialogOpen}
        onClose={handleCloseDeleteConfimrationDialog}
        onDelete={handleDeleteEstablishment}
        title={t('page.superAdmin.deleteEstablishmentConfirmationDialog.title')}
        message={t('page.superAdmin.deleteEstablishmentConfirmationDialog.message', {
          name: _.get(_.find(establishments, ['_id', selectedEstablishmentId]), 'name'),
        })}
      />
      <CreateOrdoclicEntityDialog
        isCreating={createOrdoclicEntityRequestHandler.requestTracker.loading}
        open={createOrdoclicEntityDialogOpen}
        onClose={() => setCreateOrdoclicEntityDialogOpen(false)}
        onCreateEntity={handleCreateOrdoclicEntity}
        establishmentName={_.get(_.find(establishments, ['_id', selectedEstablishmentId]), 'name') || ''}
      />
      <CreateEstablishmentDialogContainer
        open={establishmentDialogOpen}
        onClose={handleCloseEstablishmentDialog}
        onCreateEstablishment={handleUpdateEstablishment}
        isEditMode={isEditMode}
        initialValues={
          isEditMode
            ? {
                ..._.omit(_.find(establishments, ['_id', selectedEstablishmentId]), 'structureActivity'),
                structureActivity: _.get(_.find(establishments, ['_id', selectedEstablishmentId]), 'structureActivity.code') || '',
              }
            : undefined
        }
      />
      <Dialog open={showStatsDialogOpen} onClose={handleCloseStatsDialog} fullScreen={true}>
        <DialogTitle>
          {t('page.superAdmin.establishmentStats.title', { name: _.get(_.find(establishments, ['_id', selectedEstablishmentIdForStats]), 'name') })}
        </DialogTitle>
        <DialogContent>
          <StatsContainer establishmentId={selectedEstablishmentIdForStats} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatsDialog} color="primary">
            {t('common:button.close.label')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EstablishmentsTableContainer
